import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { webhooks } from '@repletoryreach/sdk-api';
import { Badge, Button, Card, Group, Skeleton, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { Drawer, Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { formatDateTime, formatRelative } from '../../../../lib/format';
import { statusTone } from '../../../../lib/status-tone';
import { useT } from '../../../../i18n/I18nProvider';

const PAGE_SIZE = 25;

/**
 * The delivery log for one webhook.
 *
 * `settings-webhooks.md` §4 item 6: a tenant whose webhook stopped firing had
 * **zero diagnostic surface** in the console. Two cursor-paged list endpoints,
 * per-delivery and per-subscription replay, HTTP status, latency and
 * `blocked_reason` all existed and none of it was reachable; the admin console
 * has had a full deliveries page since W45.
 */
export function DeliveriesDrawer({
  subscription,
  onClose
}: {
  subscription: webhooks.WebhookSubscription | null;
  onClose: () => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const [failedOnly, setFailedOnly] = useState(false);
  // How many attempts are on screen. The endpoint pages, and the drawer used
  // to show the newest 25 with no way past them, so an endpoint that failed
  // thirty times looked like it failed twenty-five.
  const [shown, setShown] = useState(PAGE_SIZE);

  // A new subscription, or a change of filter, starts from the top again.
  useEffect(() => { setShown(PAGE_SIZE); }, [subscription?.id, failedOnly]);

  const { data, isPending, isFetching, error } = useQuery({
    enabled: !!subscription,
    queryKey: ['webhook', subscription?.id, 'deliveries', failedOnly, shown],
    queryFn: () => webhooks.listDeliveries(subscription!.id, {
      limit: shown,
      // `failed` covers every terminal unhappy state server-side: a retry that
      // ran out, a 4xx that stopped it and a target the safety check refused.
      ...(failedOnly ? { result: 'failed' as const } : {})
    }),
    placeholderData: (prev) => prev,
    // Deliveries are retried on a schedule, so a page left open should follow.
    refetchInterval: 15_000
  });

  const replayOne = useMutation({
    mutationFn: (deliveryId: string) => webhooks.replayDelivery(subscription!.id, deliveryId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['webhook', subscription?.id, 'deliveries'] });
      notifySuccess(t('settings.webhooks.toast.replayed'), t('settings.nav.webhooks'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.nav.webhooks'))
  });

  const replayAll = useMutation({
    mutationFn: () => webhooks.replaySubscription(subscription!.id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['webhook', subscription?.id, 'deliveries'] });
      notifySuccess(t('settings.webhooks.toast.replayedAll', { count: res.data.requeued }), t('settings.nav.webhooks'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.nav.webhooks'))
  });

  const rows = data?.data ?? [];
  const total = data?.meta?.total ?? rows.length;
  const hasMore = rows.length < total;

  return (
    <Drawer
      opened={!!subscription}
      onClose={onClose}
      title={t('settings.webhooks.deliveries.title')}
      position="right"
      size="lg"
    >
      {subscription && (
        <Stack gap="md" style={{ padding: 4 }}>
          <Text role="caption" c="var(--rr-color-text-muted)" style={{ wordBreak: 'break-all' }}>
            {subscription.url}
          </Text>

          <Group gap="xs" wrap="wrap">
            <Button
              size="xs"
              variant={failedOnly ? 'filled' : 'default'}
              onClick={() => setFailedOnly((v) => !v)}
            >
              {t('settings.webhooks.deliveries.failedOnly')}
            </Button>
            {/* §4 item 7: replay existed and was unreachable, so a receiver
                that was down for an hour could not be caught up. */}
            <Button size="xs" variant="default" loading={replayAll.isPending} onClick={() => replayAll.mutate()}>
              {t('settings.webhooks.deliveries.replayAll')}
            </Button>
          </Group>

          {error && <Alert color="error" role="alert">{t('settings.webhooks.deliveries.error')}</Alert>}

          {isPending && <Stack gap="xs">{[0, 1, 2].map((i) => <Skeleton key={i} height={64} />)}</Stack>}

          {!isPending && !error && rows.length === 0 && (
            <EmptyState
              title={failedOnly ? t('settings.webhooks.deliveries.noFailures') : t('settings.webhooks.deliveries.empty')}
              description={failedOnly ? undefined : t('settings.webhooks.deliveries.empty.hint')}
            />
          )}

          {rows.map((d) => (
            <Card key={d.id} withBorder>
              <Stack gap={6}>
                <Group justify="space-between" wrap="nowrap">
                  <Group gap={6} wrap="wrap">
                    <StatusBadge status={statusTone(d.result)}>
                      {t(`settings.webhooks.result.${d.result}`, d.result)}
                    </StatusBadge>
                    <Badge size="xs" variant="light">{d.event_type}</Badge>
                    {d.attempt_number > 1 && (
                      <Badge size="xs" variant="light">
                        {t('settings.webhooks.deliveries.attempt', { n: d.attempt_number })}
                      </Badge>
                    )}
                  </Group>
                  <Tooltip label={formatDateTime(d.created_at)} position="left">
                    <Text role="caption" c="var(--rr-color-text-muted)">{formatRelative(d.created_at)}</Text>
                  </Tooltip>
                </Group>

                <Group gap="md" wrap="wrap">
                  {d.http_status !== null && d.http_status !== undefined && (
                    <Text role="caption">
                      {t('settings.webhooks.deliveries.status', { status: d.http_status })}
                    </Text>
                  )}
                  {d.latency_ms !== null && d.latency_ms !== undefined && (
                    <Text role="caption" c="var(--rr-color-text-muted)">
                      {t('settings.webhooks.deliveries.latency', { ms: d.latency_ms })}
                    </Text>
                  )}
                </Group>

                {/* The SSRF guard's refusal, which otherwise looks like a
                    delivery that simply never happened. */}
                {d.blocked_reason && (
                  <Alert color="warning">
                    {t(`settings.webhooks.blocked.${d.blocked_reason}`, d.blocked_reason)}
                  </Alert>
                )}

                {/* What the receiver said. The status code names the failure;
                    this is the only place the reason for it shows up, and the
                    service stored it from the first delivery without ever
                    putting it on the wire. */}
                {d.response_body_excerpt && (
                  <Stack gap={4}>
                    <Text role="caption" c="var(--rr-color-text-muted)">
                      {t('settings.webhooks.deliveries.response')}
                    </Text>
                    <pre className="rr-webhooks__response">{d.response_body_excerpt}</pre>
                  </Stack>
                )}


                {/* Replay is offered on every attempt that did not arrive,
                    which includes a 4xx that stopped the retries and a target
                    the safety check refused, not only the two states the
                    button used to check for. */}
                {(d.result === 'failed' || d.result === 'dead'
                  || d.result === 'blacklisted' || d.result === 'blocked') && (
                  <div>
                    <Button
                      size="xs"
                      variant="subtle"
                      loading={replayOne.isPending && replayOne.variables === d.id}
                      onClick={() => replayOne.mutate(d.id)}
                    >
                      {t('settings.webhooks.deliveries.replay')}
                    </Button>
                  </div>
                )}
              </Stack>
            </Card>
          ))}

          {rows.length > 0 && (
            <Group justify="space-between" align="center" wrap="wrap" gap="sm">
              <Text role="caption" c="var(--rr-color-text-muted)">
                {t('settings.webhooks.deliveries.showing', { shown: rows.length, total })}
              </Text>
              {hasMore && (
                <Button
                  size="xs"
                  variant="default"
                  loading={isFetching}
                  onClick={() => setShown((n) => n + PAGE_SIZE)}
                >
                  {t('action.loadMore')}
                </Button>
              )}
            </Group>
          )}
        </Stack>
      )}
    </Drawer>
  );
}
